<template>
    <b-modal id="bv-modal-quick-message" size="lg" :title="isEditing ? 'Editar mensagem rápida' : 'Adicionar nova mensagem rápida'" scrollable centered no-stacking>
        <b-container fluid>
            <form id="quick-message-form">
                <div class="row">
                    <div class="col-md-12">
                        <div class="form-group">
                            <label for="quick-message-name">Nome <span class="text-danger">*</span></label>
                            <input
                                type="text"
                                class="form-control"
                                id="quick-message-name"
                                v-model="form.name"
                                placeholder="Nome da mensagem rápida"
                            >
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-md-12">
                        <div class="form-group">
                            <label>Mensagem <span class="text-danger">*</span></label>

                            <!-- Toolbar de variáveis -->
                            <div class="variables-toolbar">
                                <span class="variables-label">
                                    <i class="fas fa-tag"></i> Inserir variável:
                                </span>
                                <div class="variables-list">
                                    <button
                                        v-for="variable in variables"
                                        :key="variable.key"
                                        type="button"
                                        class="variable-btn"
                                        :title="variable.label"
                                        @click="insertVariable(variable)"
                                    >
                                        {{ variable.label }}
                                    </button>

                                    <!-- Variável de texto livre sequencial -->
                                    <button
                                        type="button"
                                        class="variable-btn variable-btn-custom"
                                        @click="insertFreeText()"
                                        title="Insere um campo de texto livre numerado sequencialmente"
                                    >
                                        <i class="fas fa-pencil-alt"></i> Texto livre
                                    </button>
                                </div>
                            </div>

                            <TextEditor
                                ref="textEditor"
                                v-model="form.message"
                                placeholder="Digite o conteúdo da mensagem rápida..."
                                editorId="quick-message-editor"
                            ></TextEditor>

                            <!-- Preview das variáveis presentes na mensagem -->
                            <div class="variables-legend" v-if="detectedVariables.length > 0">
                                <span class="variables-legend-title">
                                    <i class="fas fa-info-circle"></i> Variáveis detectadas:
                                </span>
                                <span
                                    v-for="v in detectedVariables"
                                    :key="v.key"
                                    class="variable-legend-badge"
                                    :class="v.isCustom ? 'badge-custom' : 'badge-system'"
                                    :title="v.isCustom ? 'Texto livre' : 'Campo da solicitação: ' + v.key"
                                >
                                    {{ v.label }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-md-12">
                        <div class="form-group">
                            <label>Grupos</label>
                            <small class="text-muted d-block mb-2">
                                <i class="fas fa-info-circle"></i>
                                Se nenhum grupo for selecionado, a mensagem estará disponível para todos os grupos.
                            </small>
                            <div class="multiselect-container">
                                <input
                                    type="text"
                                    v-model="searchTerm"
                                    placeholder="Pesquisar grupos..."
                                    class="form-control mb-2"
                                >
                                <div class="checkbox-list">
                                    <div v-if="filteredGroups.length === 0" class="text-muted text-center py-2">
                                        Nenhum grupo encontrado.
                                    </div>
                                    <div v-for="group in filteredGroups" :key="group.id" class="form-check">
                                        <input
                                            class="form-check-input"
                                            type="checkbox"
                                            :id="`qm-group-${group.id}`"
                                            :value="group.id"
                                            v-model="form.groups"
                                        >
                                        <label class="form-check-label" :for="`qm-group-${group.id}`">
                                            {{ group.name }}
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div v-if="selectedGroups.length > 0" class="badges-container">
                                <span
                                    v-for="group in selectedGroups"
                                    :key="group.id"
                                    class="badge badge-group"
                                >
                                    {{ group.name }}
                                    <span class="badge-remove" @click="removeGroup(group.id)" title="Remover">&times;</span>
                                </span>
                            </div>
                            <small v-else class="text-muted">Nenhum grupo selecionado — disponível para todos.</small>
                        </div>
                    </div>
                </div>
            </form>
        </b-container>

        <template #modal-footer>
            <div class="modal-footer-wrapper">
                <button type="submit" @click.prevent="submitForm" class="btn btn-primary" :disabled="loading">
                    <span v-if="loading">
                        <i class="fas fa-spinner fa-pulse"></i> Aguarde...
                    </span>
                    <span v-else>
                        <i class="fas fa-save"></i> Salvar
                    </span>
                </button>

                <button v-if="isEditing" type="button" @click.prevent="deleteItem" class="btn btn-danger" :disabled="loading">
                    <i class="fas fa-trash-alt"></i> Deletar
                </button>
            </div>
        </template>
    </b-modal>
</template>

<script>
import TextEditor from '../shared/TextEditor.vue';

const SYSTEM_VARIABLES = [
    { key: 'NomeCliente', label: 'Nome Fantasia'   },
    { key: 'sector',      label: 'Setor'           },
    { key: 'Base',        label: 'Base'            },
    { key: 'nomePonto',   label: 'Nome do Ponto'   },
    { key: 'codigoPonto', label: 'Código do Ponto' },
    { key: 'cep',         label: 'CEP'             },
    { key: 'endereco',    label: 'Endereço'        },
    { key: 'status',      label: 'Status'          },
    { key: 'chamado',     label: 'Chamado'         },
    { key: 'protocolo',   label: 'Protocolo'       },
    { key: 'cnpj',        label: 'CNPJ'            },
    { key: 'updated_by',  label: 'Alterado por'    },
];

export default {
    name: "QuickMessageModal",
    components: { TextEditor },
    data() {
        return {
            loading: false,
            allGroups: [],
            dataEdit: {},
            searchTerm: '',
            form: {
                name: '',
                message: '',
                groups: [],
            },
            variables: SYSTEM_VARIABLES,
        }
    },
    computed: {
        isEditing() {
            return this.dataEdit && this.dataEdit.id;
        },
        filteredGroups() {
            if (!this.searchTerm) return this.allGroups;
            const term = this.searchTerm.toLowerCase();
            return this.allGroups.filter(g => g.name.toLowerCase().includes(term));
        },
        selectedGroups() {
            return this.allGroups.filter(g => this.form.groups.includes(g.id));
        },
        detectedVariables() {
            const content = this.form.message || '';
            const matches = [...content.matchAll(/\{\{([^}]+)\}\}/g)];
            const seen = new Set();
            const result = [];

            for (const match of matches) {
                const key = match[1];
                if (seen.has(key)) continue;
                seen.add(key);

                const system = SYSTEM_VARIABLES.find(v => v.key === key);
                if (system) {
                    result.push({ key, label: system.label, isCustom: false });
                } else {
                    result.push({ key, label: key, isCustom: true });
                }
            }

            return result;
        },
    },
    watch: {
        'form.message'(newVal) {
            const matches = [...(newVal || '').matchAll(/\{\{texto(\d+)\}\}/g)];
            const nums = matches.map(m => parseInt(m[1]));
            const sorted = [...nums].sort((a, b) => a - b);
            const hasGap = sorted.some((n, i) => n !== i + 1);
            if (hasGap) {
                this.$nextTick(() => this.renumberFreeTexts());
            }
        },
    },
    methods: {
        // --- Variáveis ---
        insertVariable(variable) {
            this.insertIntoEditor(`{{${variable.key}}}`);
        },
        insertFreeText() {
            const currentContent = this.$refs.textEditor
                ? this.$refs.textEditor.getContent()
                : '';
            const matches = [...currentContent.matchAll(/\{\{texto(\d+)\}\}/g)];
            const nums = matches.map(m => parseInt(m[1]));
            const nextIndex = nums.length ? Math.max(...nums) + 1 : 1;
            this.insertIntoEditor(`{{texto${nextIndex}}}`);
        },
        renumberFreeTexts() {
            const editor = document.getElementById('quick-message-editor');
            if (!editor) return;

            let counter = 1;
            editor.querySelectorAll('.qm-variable-tag').forEach(span => {
                if (/^\{\{texto\d+\}\}$/.test(span.dataset.variable)) {
                    const newKey = `{{texto${counter}}}`;
                    span.dataset.variable = newKey;
                    span.textContent = newKey;
                    counter++;
                }
            });

            if (this.$refs.textEditor) {
                this.form.message = this.$refs.textEditor.getContent();
            }
        },
        insertIntoEditor(text) {
            const editor = document.getElementById('quick-message-editor');
            if (!editor) return;

            editor.focus();

            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0 && editor.contains(selection.anchorNode)) {
                const range = selection.getRangeAt(0);
                range.deleteContents();

                const span = document.createElement('span');
                span.className = 'qm-variable-tag';
                span.contentEditable = 'false';
                span.dataset.variable = text;
                span.textContent = text;
                range.insertNode(span);

                range.setStartAfter(span);
                range.collapse(true);
                selection.removeAllRanges();
                selection.addRange(range);
            } else {
                editor.innerHTML += `<span class="qm-variable-tag" contenteditable="false" data-variable="${text}">${text}</span>`;
            }

            this.$nextTick(() => {
                if (this.$refs.textEditor) {
                    this.form.message = this.$refs.textEditor.getContent();
                }
            });
        },

        // --- Grupos ---
        async fetchGroups() {
            await axios.get("/settings/contacts/groups/type/email").then(response => {
                this.allGroups = response.data.map(g => ({ id: g.id, name: g.name }));
            }).catch(error => {
                console.error(error);
                Swal.fire({ type: 'error', text: 'Falha ao carregar grupos!' });
            });
        },
        removeGroup(groupId) {
            this.form.groups = this.form.groups.filter(id => id !== groupId);
        },
        async openModal(data = null) {
            this.resetForm();
            await this.fetchGroups();
            if (data) {
                this.dataEdit = data;
                this.loadFormData(data);
            }
            this.$bvModal.show('bv-modal-quick-message');

            this.$nextTick(() => {
                if (this.$refs.textEditor) {
                    this.$refs.textEditor.setContent(this.form.message);
                }
            });
        },
        closeModal(data = null) {
            this.loading = false;
            this.resetForm();
            if (data) this.$emit("updateTable", data);
            this.$bvModal.hide('bv-modal-quick-message');
        },
        loadFormData(data) {
            this.form.id      = data.id;
            this.form.name    = data.name;
            this.form.message = data.message;
            this.form.groups  = data.groups
                ? data.groups.map(g => (typeof g === 'object' ? g.id : g))
                : [];
        },
        resetForm() {
            this.form = { name: '', message: '', groups: [] };
            this.searchTerm = '';
            this.dataEdit   = {};

            if (this.$refs.textEditor) {
                this.$refs.textEditor.clearContent();
            }
        },
        validateForm() {
            if (!this.form.name || !this.form.name.trim()) {
                Swal.fire({ type: 'error', text: "Campo 'Nome' é obrigatório!" });
                return false;
            }
            const messageText = this.$refs.textEditor
                ? this.$refs.textEditor.getContent().replace(/<[^>]*>/g, '').trim()
                : '';
            if (!messageText) {
                Swal.fire({ type: 'error', text: "Campo 'Mensagem' é obrigatório!" });
                return false;
            }
            return true;
        },
        async deleteItem() {
            const { value } = await Swal.fire({
                title: 'Deseja deletar esta mensagem rápida?',
                text: 'Esta ação não poderá ser desfeita.',
                type: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Sim, deletar',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#dc3545',
                reverseButtons: true,
                showCloseButton: true,
            });

            if (!value) return;
            this.loading = true;

            try {
                await axios.delete(`/settings/quick-messages/${this.form.id}`);
                Swal.fire({ type: 'success', text: 'Mensagem rápida deletada com sucesso.' });
                this.$emit("deleteItem", this.form.id);
                this.closeModal();
            } catch (error) {
                console.error(error);
                const message = error.response?.data?.error || 'Falha ao deletar mensagem rápida!';
                Swal.fire({ type: 'error', text: message });
                this.loading = false;
            }
        },
        async submitForm() {
            if (!this.validateForm()) return;
            this.loading = true;

            const formData = {
                name:    this.form.name,
                message: this.$refs.textEditor ? this.$refs.textEditor.getContent() : this.form.message,
                groups:  this.form.groups,
            };

            try {
                let response;
                if (this.form.id) {
                    response = await axios.put(`/settings/quick-messages/${this.form.id}`, formData);
                    Swal.fire({ type: 'success', text: 'Mensagem rápida atualizada com sucesso.' });
                } else {
                    response = await axios.post("/settings/quick-messages", formData);
                    Swal.fire({ type: 'success', text: 'Mensagem rápida salva com sucesso.' });
                }
                this.closeModal(response.data);
            } catch (error) {
                console.error(error);
                const message = error.response?.data?.error
                    ? Object.values(error.response.data.error).flat().join('\n')
                    : 'Falha ao salvar mensagem rápida! Valide todos os campos e tente novamente.';
                Swal.fire({ type: 'error', text: message });
                this.loading = false;
            }
        },
    },
}
</script>

<style scoped>
.checkbox-list {
    max-height: 180px;
    overflow-y: auto;
    border: 1px solid #ced4da;
    border-radius: 4px;
    padding: 10px;
}

.badges-container { margin-top: 8px; }

.badge-group {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin: 2px 4px;
    font-size: 0.85em;
    padding: 4px 8px;
    background-color: #dc3545;
    color: #ffffff;
}

.badge-remove {
    cursor: pointer;
    font-size: 1em;
    line-height: 1;
    opacity: 0.75;
}

.badge-remove:hover { opacity: 1; }

.modal-footer-wrapper {
    display: flex;
    flex-direction: row-reverse;
    justify-content: space-between;
    width: 100%;
}

/* --- Variables toolbar --- */
.variables-toolbar {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    flex-wrap: wrap;
    background: #f8f9fa;
    border: 1px solid #ced4da;
    border-bottom: none;
    border-radius: 4px 4px 0 0;
    padding: 8px 10px;
}

.variables-label {
    font-size: 11px;
    font-weight: 700;
    color: #555;
    white-space: nowrap;
    padding-top: 3px;
}

.variables-list {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    align-items: center;
}

.variable-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 9px;
    font-size: 11px;
    font-weight: 600;
    background: #e8f4f8;
    color: #1c869a;
    border: 1px solid #b2d8e0;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.15s;
    line-height: 1.4;
}

.variable-btn:hover {
    background: #1c869a;
    color: #fff;
    border-color: #1c869a;
}

.variable-btn-custom {
    background: #fff3cd;
    color: #856404;
    border-color: #ffc107;
}

.variable-btn-custom:hover {
    background: #ffc107;
    color: #333;
    border-color: #ffc107;
}

/* Legend de variáveis detectadas */
.variables-legend {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-top: 6px;
    padding: 6px 10px;
    background: #f0fafa;
    border: 1px solid #c8e6ea;
    border-top: none;
    border-radius: 0 0 4px 4px;
    font-size: 11px;
}

.variables-legend-title {
    font-weight: 700;
    color: #555;
    white-space: nowrap;
}

.variable-legend-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
}

.badge-system {
    background: #d0eef5;
    color: #1c6f80;
    border: 1px solid #9dd4e0;
}

.badge-custom {
    background: #fff3cd;
    color: #856404;
    border: 1px solid #ffc107;
}
</style>

<style>
/* Global: estilo do span de variável dentro do contenteditable */
.qm-variable-tag {
    display: inline-block;
    background: #d0eef5;
    color: #1c6f80;
    border: 1px solid #9dd4e0;
    border-radius: 10px;
    padding: 1px 8px;
    font-size: 12px;
    font-weight: 600;
    margin: 0 2px;
    user-select: none;
    cursor: default;
}
</style>